import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import Checkbox from "@mui/material/Checkbox";
import Divider from "@mui/material/Divider";
import Input from "@mui/material/Input";
import ListItem from "@mui/material/ListItem";
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker';
import moment from 'moment';
import React, { useRef, useState } from "react";
import { useDrag, useDrop } from 'react-dnd';
import { default as todoService } from "./todo.service";

export default function TodoListItem(props) {
	const [todo, setTodo] = useState(props.todo);
	// const snackbar = useSnackbar();
	const ref = useRef();

	const [dragProps, drag] = useDrag(() => ({
		type: 'todoListItem',
		item: todo,
		collect: (monitor) => ({
			isDragging: !!monitor.isDragging()
		})
	}));

	const [dropProps, drop] = useDrop(() => ({
		accept: 'todoListItem',
		drop: (todo) => props.todoDropped?.(todo),
		collect: (monitor) => ({
			isOver: !!monitor.isOver()
		}),
		hover: (item, monitor) => {
			if (!ref.current) {
				return;
			}

			const dragIndex = item.ordinal;
			const hoverIndex = todo.ordinal;
			// Don't replace items with themselves
			if (dragIndex === hoverIndex) {
				return;
			}
			// Determine rectangle on screen
			const hoverBoundingRect = ref.current?.getBoundingClientRect();
			// Get vertical middle
			const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
			// Determine mouse position
			const clientOffset = monitor.getClientOffset();
			// Get pixels to the top
			const hoverClientY = clientOffset.y - hoverBoundingRect.top;
			// Only perform the move when the mouse has crossed half of the items height
			// When dragging downwards, only move when the cursor is below 50%
			// When dragging upwards, only move when the cursor is above 50%
			// Dragging downwards
			if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
				return;
			}
			// Dragging upwards
			if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
				return;
			}
			// Time to actually perform the action
			props.todoDragged?.(item.id, todo.id);
			// Note: we're mutating the monitor item here!
			// Generally it's better to avoid mutations,
			// but it's good here for the sake of performance
			// to avoid expensive index searches.
			item.ordinal = hoverIndex;
		},
	}));

	const onToggle = async () => {
		todo.complete = !todo.complete;
		save(todo);
		setTodo({ ...todo });
	};

	const onChange = async (e, key) => {
		let value = e?.target ? e?.target.value : e;
		value = value instanceof moment ? value.valueOf() : value;
		todo[key] = value;
		props?.onChange?.(todo);
		setTodo({ ...todo });
	};

	const onEnter = async (e) => {
		if (e.keyCode == 13 && todo.item) {
			save();
			if (todo.id) {
				//e.preventDefault();
				e.target.blur();
			}
		}
	}

	const save = async () => {
		if( !todo.item ){
			return;
		}
		let result = await todoService.save(todo);
		//snackbar.success('Todo item saved successfully');
		props?.onSave?.(result.data.data);
		if (!todo.id) {
			setTodo({});
		}
	}

	const onDelete = async () => {
		let result = await todoService.delete(todo.id);
		//snackbar.success('Todo item removed successfully');
		props?.onDelete?.(result);
	}

	let dueDate = todo?.dueDate ? moment(todo.dueDate) : null;
	let itemStyle = {
		opacity: dragProps.isDragging ? 0 : 1,
		textDecoration: todo.complete ? 'line-through' : '',
		p: 0,
	};
	let dateStyle = {
		'.MuiOutlinedInput-root': { px: 1, py: 0, fontSize: '0.75rem' },
		'.MuiOutlinedInput-notchedOutline': { borderWidth: 0 },
		'.MuiInputAdornment-root': { ml: 0, width: 20, justifyContent: 'end' },
		'.MuiFormLabel-root': {overflow: 'visible'},
		width: 70,
		p: 0,
		ml: 2,
	};

	drag(drop(ref));
	return (
		<React.Fragment key={todo.id}>
			<ListItem className="hover-toggle" sx={itemStyle} ref={ref}>
				<DragIndicatorIcon className="hover" />
				<Checkbox 
					sx={{ p: 0.5 }} 
					checked={todo.complete} 
					onClick={() => onToggle()} 
					disabled={todo.id == null} />
				<Input
					multiline={true}
					sx={{ height: 'auto', '.MuiInputBase-input':{textOverflow:'ellipsis'} }}
					placeholder={todo.id ? '' : 'Add New'}
					value={todo.item || ''}
					fullWidth
					disableUnderline
					onChange={(e) => onChange(e, 'item')}
					onKeyDown={(e) => onEnter(e)} 
					onBlur={(e)=>save(e)} />
				<MobileDatePicker
					format="MMM D"
					className={todo.id || todo.dueDate ? '' : 'hover'}
					label={todo.dueDate ? '' : 'Due'}
					value={dueDate}
					variant="standard"
					disableUnderline
					views={['month', 'year', 'day']}
					onChange={(e) => { onChange(e, 'dueDate'); save(); }} sx={dateStyle} />

				{todo.id ? <DeleteIcon className="hover" onClick={() => onDelete()} /> : ''}
			</ListItem>
			{todo.id ? <Divider /> : ''}
		</React.Fragment>
	);
}
